import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {usePageTypePathsMap} from './RouterContext';
import {getPageType} from './getCurrentRouteFromUrl';
import {PageTypes} from './types';
import {usePathname} from './useLocation';

type PageTypeCache = Map<string, PageTypes | undefined>;
type PageTypesContextType = {
  pageTypes: PageTypes | undefined;
  initialPageType: PageTypes | undefined;
  isDirectEntryPageType: boolean;
};

const PageTypeContext = createContext<PageTypesContextType>({
  pageTypes: undefined,
  initialPageType: undefined,
  isDirectEntryPageType: true,
});
const PageTypeCallbackContext = createContext<() => PageTypesContextType>(
  () => {
    throw new Error('CurrentPageTypeCallback context is not implemented.');
  },
);

export function PageTypeContextProvider({children}: PropsWithChildren) {
  const pageTypePerUrlCache = useRef<PageTypeCache>(new Map());
  const pathname = usePathname();
  const pageTypePathsMap = usePageTypePathsMap();
  const isDirectEntryPageType = useRef(true);

  const getPageTypeByPathname = useCallback(
    (pathname: string) => {
      const currentPageType =
        pageTypePerUrlCache.current.get(pathname) ??
        getPageType(pageTypePathsMap, pathname);
      if (!pageTypePerUrlCache.current.has(pathname)) {
        pageTypePerUrlCache.current.set(pathname, currentPageType);
      }
      return currentPageType;
    },
    [pageTypePathsMap],
  );

  const initialPageType = useMemo(() => getPageTypeByPathname(pathname), []);
  const currentPageType = useMemo(
    () => getPageTypeByPathname(pathname),
    [getPageTypeByPathname, pathname],
  );

  useEffect(() => {
    if (initialPageType !== currentPageType && isDirectEntryPageType.current) {
      isDirectEntryPageType.current = false;
    }
  }, [initialPageType, currentPageType]);

  const value = useMemo(() => {
    return {
      pageTypes: currentPageType,
      initialPageType,
      isDirectEntryPageType: isDirectEntryPageType.current,
    };
  }, [currentPageType, initialPageType, isDirectEntryPageType.current]);

  const valueCallback = useCallback(() => {
    return {
      pageTypes: currentPageType,
      initialPageType,
      isDirectEntryPageType: isDirectEntryPageType.current,
    };
  }, [currentPageType, initialPageType, isDirectEntryPageType.current]);

  return (
    <PageTypeContext.Provider value={value}>
      <PageTypeCallbackContext.Provider value={valueCallback}>
        {children}
      </PageTypeCallbackContext.Provider>
    </PageTypeContext.Provider>
  );
}

export const useCurrentPageTypeCallback = () => {
  const currentPageTypeCallback = useContext(PageTypeCallbackContext);
  return useCallback(
    () => currentPageTypeCallback().pageTypes,
    [currentPageTypeCallback],
  );
};

/**
 * Returns the current page type and re-renders the component in case the page type changes.
 */
export const useCurrentPageType = (): PageTypes | undefined => {
  return useContext(PageTypeContext).pageTypes;
};

export const useInitialPageType = (): PageTypes | undefined => {
  return useContext(PageTypeContext).initialPageType;
};

export const useIsInitialPageType = () => {
  const currentPageType = useCurrentPageType();
  const initialPageType = useInitialPageType();
  return currentPageType === initialPageType;
};

/**
 * @returns if the user is still on the same page type where they originally landed,
 * it will return false when the user navigates away from the page type
 */
export const useIsDirectEntryPageType = () =>
  useContext(PageTypeContext).isDirectEntryPageType;
